/**
 * Keys whose values must never reach a log transport. Matched case-insensitively
 * against object keys at any depth.
 *
 * These are the credential- and PII-bearing headers attached to outbound
 * requests by createAppServerAxiosConfig / createBcomAxiosConfig / createApimAxiosConfig,
 * plus their inbound equivalents on a Next.js request object.
 */
export const REDACTED_KEYS = [
  'cookie',
  'set-cookie',
  'x-booking-iam-access-token',
  'authorization',
  'x-authorization',
  'x-api-key',
  'x-forwarded-for',
  'x-azure-clientip',
  // Node's ClientRequest, reachable as error.request: the whole raw request
  // head as one string, Cookie line included. Its value cannot be redacted
  // key-by-key, so the field goes entirely.
  '_header',
] as const;

export const REDACTED = '[REDACTED]';

const MAX_DEPTH = 8;

export const MAX_DEPTH_EXCEEDED = '[max depth exceeded]';

const CIRCULAR = '[Circular]';

const redactedKeySet = new Set<string>(REDACTED_KEYS);

const isRedactedKey = (key: string) => redactedKeySet.has(key.toLowerCase());

/**
 * Deep-copies `value`, replacing the value of any redacted key with `[REDACTED]`.
 *
 * Walks whatever json() would serialise — plain objects, arrays, `Map`/`Headers`,
 * `toJSON` hooks, `Error` own props. Anything it skips but json() emits is a hole.
 *
 * `seen` holds each object's redacted copy, not just a visited flag: an axios
 * error reaches the same `config` twice (`error.config` and
 * `error.response.config` are one object), so returning the original on the
 * second visit would ship the headers this strips.
 */
export const redact = <T>(
  value: T,
  depth = 0,
  seen = new Map<object, unknown>()
): T => {
  if (value === null || typeof value !== 'object') return value;
  // Not `return value`: that would pass a Cookie nested this deep through in
  // the clear.
  if (depth >= MAX_DEPTH) return MAX_DEPTH_EXCEEDED as unknown as T;
  if (seen.has(value)) return seen.get(value) as T;

  if (Array.isArray(value)) {
    const copy: unknown[] = [];
    // Registered before recursing so a cycle resolves to the copy in progress.
    seen.set(value, copy);

    value.forEach((item) => copy.push(redact(item, depth + 1, seen)));

    return copy as unknown as T;
  }

  // Headers/Map-like: expose entries as a plain object so the keys can be seen.
  if (isEntriesIterable(value)) {
    const copy: Record<string, unknown> = {};
    seen.set(value, copy);

    for (const [key, entryValue] of value.entries()) {
      copy[String(key)] = isRedactedKey(String(key))
        ? REDACTED
        : redact(entryValue, depth + 1, seen);
    }

    return copy as unknown as T;
  }

  // json() calls toJSON, and AxiosError's returns `config` headers and all, so
  // the hook's output is what needs redacting — not the instance.
  if (hasToJSON(value)) {
    // A sentinel, not the copy-in-progress: the output does not exist yet.
    seen.set(value, CIRCULAR);

    const json = value.toJSON();

    // A hook returning `this` has told us nothing; fall through to the walk
    // below rather than handing back the instance with its values in the clear.
    if (json !== value) {
      const copy = redact(json, depth, seen);

      seen.set(value, copy);

      return copy as unknown as T;
    }
  }

  // Every remaining object — plain, Error, or any class instance — is walked by
  // its own enumerable props, because that is what json() serialises. Returning
  // a class instance as-is would leak: a ClientRequest keeps the raw request
  // head, Cookie line and all, on its own enumerable `_header`. Non-enumerable
  // props (an Error's name/message/stack) are skipped here and by json() alike.
  const copy: Record<string, unknown> = {};
  seen.set(value, copy);

  for (const [key, entryValue] of Object.entries(value)) {
    copy[key] = isRedactedKey(key)
      ? REDACTED
      : redact(entryValue, depth + 1, seen);
  }

  return copy as unknown as T;
};

const hasToJSON = (value: object): value is { toJSON: () => unknown } =>
  typeof (value as { toJSON?: unknown }).toJSON === 'function';

const isEntriesIterable = (
  value: object
): value is { entries: () => Iterable<[unknown, unknown]> } =>
  typeof (value as { entries?: unknown }).entries === 'function' &&
  (value instanceof Map || value instanceof Headers);
